import { interval, Value } from "baconjs"

import { Http } from "@encoway/sales-showroom-auth"

import { ResponseError } from "../ResponseError"
import { Constants } from "../constants"
import { mappingOptions } from "./mappingOptions"
import { ValueFormat } from "./types"
import { Link } from "./types/common/Link"
import { UpdateRequest } from "./types/request/UpdateRequest"
import { UserSettings } from "./types/request/UserSettings"
import { Operation } from "./types/request/operation/Operation"
import { OperationKey } from "./types/request/operation/OperationKey"
import { ConfigurationResult } from "./types/response/configurationResult/ConfigurationResult"
import { ConfigurationStatus } from "./types/response/configurationStatus/ConfigurationStatus"
import { ContainerTO } from "./types/response/container/Container"
import { ParameterTO } from "./types/response/parameter/Parameter"
import { ParameterInformation } from "./types/response/parameterInformation/ParameterInformation"
import { ParameterStatus } from "./types/response/parameterInformation/ParameterStatus"
import { StopConfigurationResponse } from "./types/response/stopConfigurationResponse/StopConfigurationResponse"
import { GuiTO } from "./types/response/tree/Gui"
import { InternalTree } from "./types/response/tree/InternalTree"
import { LinkedTree } from "./types/response/tree/LinkedTree"
import { Unit } from "./types/response/unit/Unit"
import { UpdateResponse } from "./types/response/updateResponse/UpdateResponse"

/**
 * The heartbeat interval in ms.
 * @private
 */
const heartbeatInterval = 3 * 60 * 1000

const Status = {
    Success: "SUCCESS",
    Unconfirmed: "UNCONFIRMED"
}

/**
 * Handle exception from catalog service.
 * @param response {Object} the response
 * @return response.json() or throws an Exception
 * @private
 */
function handleException(response: Response) {
    if (response.ok !== true) {
        throw new ResponseError(`Error: ${response.statusText}`, {} as Response)
    }

    return response.json()
}

/**
 * Handle exception from catalog service.
 * @param response {Object} the response
 * @return response.json() or throws an Exception
 * @private
 */
function handleEmptyBodyException(response: any) {
    if (response.ok !== true) {
        throw new ResponseError(`Error: ${response.statusText}`, {} as Response)
    }
}

/**
 * Handle exception from catalog service.
 * @param json {Object} the response from the service
 * @return json or throws an Exception
 * @private
 */
function handleServiceException(json: any) {
    if (
        Object.prototype.hasOwnProperty.call(json, "status") &&
        json.status !== Status.Success
    ) {
        throw new ResponseError(json.message || json.status, json)
    }
    return json
}

export type MappingProfile = Exclude<
    | keyof ParameterTO
    | keyof GuiTO
    | keyof ContainerTO
    | keyof Unit
    | keyof Value<any>,
    "id" | "links"
>
export type MediumMappingProfile =
    | "baseUnit"
    | "displayUnit"
    | "translatedUnit"
    | "values"
    | "facets"
    | "translatedName"
    | "readyState"
    | "viewPort"
    | "valueType"
    | "minValue"
    | "maxValue"
    | "possibleUnits"
    | "shortText"
    | "longText"
    | "imageUrl"
    | "children"

type Mapped<T, MAPPING_PROFILE> = {
    [P in Extract<MAPPING_PROFILE, keyof T>]: T[P]
} & { id: string; links: Link[] }

type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>
}

type RequestOptions = {
    configurationId?: string
}

/**
 * The Configuration Service REST API implementation.
 *
 * @class ConfigurationService
 * @param {String} baseUrl The base url, e.g. http://localhost:8080/showcase/
 * @param {Object} cfg the configuration status
 * @param {String} lang the language
 */
export class ConfigurationService<
    T extends MappingProfile = MediumMappingProfile
> {
    private http: Http
    private baseUrl: string
    /**
     * @deprecated This should be private but others depend on this.
     */
    public cfg: any
    private lang: () => string

    /**
     * @deprecated This should be private but others depend on this.
     */
    public isMain: boolean
    private multistageConfiguration: boolean
    private linkedConfiguration: boolean
    /**
     * @deprecated This should be private but others depend on this.
     */
    public configurationId: string
    private options: any
    private subcontainer: string
    /**
     * @deprecated This should be private but others depend on this.
     */
    public disposeHeartbeat: any

    constructor(
        http: Http,
        baseUrl: string,
        cfg: any,
        lang: (() => string) | string,
        isMain?: boolean
    ) {
        this.http = http
        this.baseUrl = baseUrl
        this.cfg = cfg

        this.multistageConfiguration =
            (cfg.configuration &&
                cfg.configuration.configurationEngine === "DEPSOLVER") ||
            (cfg.configurationStatus &&
                cfg.configurationStatus.configuration.configurationEngine ===
                    "DEPSOLVER") ||
            false

        this.linkedConfiguration =
            (cfg.configuration && cfg.configuration.linkedConfiguration) ||
            (cfg.configurationStatus &&
                cfg.configurationStatus.configuration.linkedConfiguration) ||
            false

        this.configurationId =
            cfg.id ||
            cfg.configurationId ||
            cfg.configurationStatus.configurationId
        this.lang = typeof lang === "function" ? lang : () => lang
        this.isMain = isMain || false
        this.options = {}
        this.subcontainer =
            (this.multistageConfiguration && cfg.subcontainer) ||
            cfg.properties?.containerId ||
            null

        if (this.isMain === true) {
            this.disposeHeartbeat = interval(heartbeatInterval, null).onValue(
                () => this.status()
            )
        }
    }

    /**
     * Set configuration service options.
     * @example
     * {
     *   "mappingOptions": {
     *      "containerMappingType": Contansts.ContainerMappingType.Unfiltered,
     *      "parameterMappingType": Constants.ParameterMappingType.Unfiltered,
     *      "valueMappingType": Constants.ValueMappingType.All,
     *      "translationMappingType": Constants.TranslationMappingType.Translations,
     *      "mappingProfile":  Constants.MappingProfile.Medium
     *   },
     *   gui: Constants.InternalUI,
     *   mediaLink: id => `http://myurl/${id}`
     * }
     * @param {Object} userSettings the options.
     * @return {Object} options when no arguments are given
     */
    settings(userSettings: RecursivePartial<UserSettings>): void {
        if (arguments.length === 0) {
            return this.options
        }
        this.options = userSettings || {}
    }

    /**
     * Loads a byte-array, to restore a configuration state.
     * @param {Blob|Uint8Array} data
     * @param {Object} requestOptions additional options for request
     */
    reset(
        data: Blob | Uint8Array,
        requestOptions?: RequestOptions
    ): Promise<any> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/octet-stream",
                "Accept-Language": this.lang()
            }),
            body: data
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then(handleException)
    }

    /**
     * Updates a configuration for a given configuration id.
     * @example
     * {
     *   "guiModel" : "newGuiModel",
     *   "unitSystem" : "imperial"
     * }
     * @param {Object} props the properties to be updated
     * @param {Object} requestOptions additional options for request
     * @returns {Promise<Object>} the updated configuration TO
     */
    update(
        props: Partial<UpdateRequest>,
        requestOptions?: RequestOptions
    ): Promise<UpdateResponse> {
        const fetchParams = {
            method: "PATCH",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify(props)
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then(handleException)
    }

    /**
     * Set configuration properties.
     * @param {String} dict the dictionary with values
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    configurationProperties(
        dict: object,
        requestOptions?: RequestOptions
    ): Promise<any> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/vnd.encoway.conf.properties+json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify(dict)
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then(handleEmptyBodyException)
    }

    /**
     * Query status information.
     * @param {Object} requestOptions additional options for request
     * @returns {Promise} the status information
     */
    status(requestOptions?: RequestOptions): Promise<ConfigurationStatus> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/vnd.encoway.conf.status+json",
                "Accept-Language": this.lang()
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then(handleException)
    }

    /**
     * Returns the configuration id.
     * @return {String} id the configuration id
     */
    id(): string {
        return this.configurationId
    }

    /**
     * Returns the url of the media link resource.
     * @param id {String} the resource id
     * @param resolution {string} the (optional) identifier for the requested image resolution
     * @return {Url} the absolute product resource url
     */
    mediaLink(id: string, resolution?: string): string {
        if (typeof this.options.mediaLink === "function") {
            return this.options.mediaLink(id, resolution)
        }
        const resourceUrl = `${this.baseUrl}/api/catalog/media?id=${id}`
        if (resolution) {
            return `${resourceUrl}&resolution=${resolution}`
        }
        return resourceUrl
    }

    /**
     * Is it a ConfiLink configuration or not.
     * @return {Boolean}
     */
    isLinkedConfiguration(): boolean {
        return this.linkedConfiguration
    }

    /**
     * Is it a multi stage configuration?
     * @return {Boolean}
     */
    isMultistageConfiguration(): boolean {
        return this.multistageConfiguration
    }

    /**
     * Go to a sub configuration or subcontainer.
     * @param {Object} link the link out of a linked tree.
     * @return {ConfigurationService} a new instance of the configuration service
     */
    goto(link?: ContainerTO): ConfigurationService<T> {
        const cs = this.linkedConfiguration
            ? new ConfigurationService<T>(
                  this.http,
                  this.baseUrl,
                  Object.assign({}, this.cfg, link),
                  this.lang,
                  false
              )
            : new ConfigurationService<T>(
                  this.http,
                  this.baseUrl,
                  Object.assign({}, this.cfg, {
                      subcontainer: link ? link.id : undefined
                  }),
                  this.lang,
                  false
              )

        cs.settings(this.options)
        return cs
    }

    /**
     * Retrieve the linked  container structure from the server.
     * @param {String} [locale] optional locale string
     * @param {Object} requestOptions additional options for request
     * @return {Promise<GuiTO>}
     */
    linkedTree(
        locale?: string,
        requestOptions?: RequestOptions
    ): Promise<LinkedTree> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/vnd.encoway.conf.src.linked+json",
                "Accept-Language": locale || this.lang()
            })
        }

        const url = mappingOptions(
            `${
                this.baseUrl
            }/api/configuration/configurations/${this.determineConfigurationId(
                requestOptions
            )}`,
            this.options
        )
        return this.http.fetch(url, fetchParams).then(handleException)
    }

    /**
     * Retrieve the internal container structure from the server.
     * @param {String} [locale] optional locale string
     * @param {Object} requestOptions additional options for request
     * @return {Promise<GuiTO>}
     */
    internalTree(
        locale?: string,
        requestOptions?: RequestOptions
    ): Promise<InternalTree> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/vnd.encoway.conf.src.internal+json",
                "Accept-Language": locale || this.lang()
            })
        }

        const url = mappingOptions(
            `${
                this.baseUrl
            }/api/configuration/configurations/${this.determineConfigurationId(
                requestOptions
            )}`,
            this.options
        )
        return this.http.fetch(url, fetchParams).then(handleException)
    }

    /**
     * Retrieve the container structure from the server.
     * @param {String} [locale] optional locale string
     * @param {Object} requestOptions additional options for request
     * @return {Promise<GuiTO>}
     */
    container(
        locale?: string,
        requestOptions?: RequestOptions
    ): Promise<GuiTO> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/vnd.encoway.conf.src.gui+json",
                "Accept-Language": locale || this.lang()
            })
        }
        let url
        if (this.subcontainer) {
            url = mappingOptions(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/containers/${this.subcontainer}`,
                this.options
            )
        } else {
            url = mappingOptions(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                this.options
            )
        }
        return this.http.fetch(url, fetchParams).then(handleException)
    }

    /**
     * Returns the ui. Either the gui model or internal tree (see settings)
     * @param {String} [locale] optional locale string
     * @param {Object} requestOptions additional options for request
     * @return {Promise<GuiTO>}
     */
    ui(
        locale?: string,
        requestOptions?: RequestOptions
    ): Promise<GuiTO | InternalTree> {
        if (this.options.gui === Constants.InternalUI) {
            return this.internalTree(locale, requestOptions)
        }

        return this.container(locale, requestOptions)
    }

    /**
     * Undo all decisions of a container.
     * @param {String} cid the container id
     * @param {Object} requestOptions additional options for request
     * @returns {Promise}
     */
    undoContainer(
        containerId: string,
        requestOptions?: RequestOptions
    ): Promise<ParameterInformation> {
        const fetchParams = {
            method: "DELETE",
            headers: new Headers({
                Accept: "application/json",
                "Accept-Language": this.lang()
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/containers/${containerId}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Set or force the value of a parameter.
     * @param {String} pid the parameter id
     * @param {String} value the value
     * @param {Constants.ValueFormat} [format] the optional format (default Formatted)
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    change(
        parameterId: string,
        value: string,
        format?: ValueFormat,
        requestOptions?: RequestOptions
    ): Promise<ParameterInformation> {
        return this.set(parameterId, value, format, requestOptions).catch(() =>
            this.force(parameterId, value, format, requestOptions)
        )
    }

    /**
     * Get the parameter value.
     * @param {String} parameterId Parameterid
     * @param {String} [locale] the optional locale
     * @param {Object} requestOptions additional options for request
     * @returns {Promise}
     */
    parameter(
        parameterId: string,
        locale?: string,
        requestOptions?: RequestOptions
    ): Promise<Mapped<ParameterTO, T>> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/json",
                "Accept-Language": locale || this.lang()
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Set the value of a parameter.
     * @param {String} parameterId the parameter id
     * @param {String} value the value
     * @param {Constants.ValueFormat} [format] the optional format (default Formatted)
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    set(
        parameterId: string,
        value: string,
        format?: ValueFormat,
        requestOptions?: RequestOptions
    ): Promise<ParameterInformation> {
        const fetchParams = {
            method: "PUT",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify({
                value,
                format: format || "FORMATTED"
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Force the value of a parameter.
     * @param {String} parameterId the parameter id
     * @param {String} value the value
     * @param {Constants.ValueFormat} [format] the optional format (default Formatted)
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    force(
        parameterId: string,
        value: string,
        format?: ValueFormat,
        requestOptions?: RequestOptions
    ): Promise<ParameterInformation> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify({
                value,
                format: format || "FORMATTED"
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Set the quantity for a parameter.
     * @param {String} parameterId the parameter id
     * @param {String} value the quantity value
     * @param {Object} requestOptions additional options for request
     * @returns {Promise}
     */
    quantity(
        parameterId: string,
        value: string,
        requestOptions?: RequestOptions
    ): Promise<ParameterInformation> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/vnd.encoway.conf.param.qty+json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify({
                quantity: value
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then((response) => {
                // Fix for quantity():
                // Status code 409 --> response.body must be returned for correct exception handling
                // to allow the use of the EPRI in the configurator
                if (response.ok !== true && response.status !== 409) {
                    throw new ResponseError(
                        `Error: ${response.statusText}`,
                        {} as any
                    )
                }

                return response.json()
            })
            .then(handleServiceException)
    }

    /**
     * Set the display unit for a parameter.
     * @param {String} parameterId the parameter id
     * @param {String} displayUnit the unit value
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    unit(
        parameterId: string,
        displayUnit: string,
        requestOptions?: RequestOptions
    ): Promise<any> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/vnd.encoway.conf.param.unit+json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify({
                unit: displayUnit
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Set parameter properties.
     * @param {String} parameterId the parameter id
     * @param {String} dict the dictionary with values
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    properties(
        parameterId: string,
        dict: object,
        requestOptions?: RequestOptions
    ): Promise<any> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type":
                    "application/vnd.encoway.conf.param.properties+json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify(dict)
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then(handleEmptyBodyException)
    }

    /**
     * Undo the value of a parameter.
     * @param parameterId {String} the parameter id
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    undo(
        parameterId: string,
        requestOptions?: RequestOptions
    ): Promise<{ status: ParameterStatus }> {
        const fetchParams = {
            method: "DELETE",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "Accept-Language": this.lang()
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}/parameters/${parameterId}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Perform a general operation.
     * @param args {Object} the operation parameters.
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    operation(
        args: Operation,
        requestOptions?: RequestOptions
    ): Promise<ParameterInformation> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/vnd.encoway.conf.operation+json",
                "Accept-Language": this.lang()
            }),
            body: JSON.stringify(args)
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Accept decision.
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    accept(requestOptions?: RequestOptions): Promise<ParameterInformation> {
        return this.operation(
            {
                operationKey: OperationKey.AcceptEnforcement
            },
            requestOptions
        )
    }

    /**
     * Decline decision.
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    decline(requestOptions?: RequestOptions): Promise<ParameterInformation> {
        return this.operation(
            {
                operationKey: OperationKey.DeclineEnforcement
            },
            requestOptions
        )
    }

    /**
     * Save the current state of the configuration.
     * @param {Object} requestOptions additional options for request
     * @return {Promise} the blob
     */
    save(requestOptions?: RequestOptions): Promise<Blob> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/octet-stream",
                "Accept-Language": this.lang()
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then((response) => response.blob())
    }

    /**
     * Representation of the configuration state enriched with additional information such as
     * the configuration status, the relevant sales context characteristics and application specific result data.
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    result(requestOptions?: RequestOptions): Promise<ConfigurationResult> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/vnd.encoway.conf.save+json",
                "Accept-Language": this.lang()
            })
        }

        return this.http
            .fetch(
                `${
                    this.baseUrl
                }/api/configuration/configurations/${this.determineConfigurationId(
                    requestOptions
                )}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Stop configuration.
     * @param {Object} requestOptions additional options for request
     * @return {Promise}
     */
    stop(requestOptions?: RequestOptions): Promise<StopConfigurationResponse> {
        const fetchParams = {
            method: "DELETE",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "Accept-Language": this.lang()
            })
        }
        if (this.disposeHeartbeat) {
            this.disposeHeartbeat()
        }
        return this.http.fetch(
            `${
                this.baseUrl
            }/api/configuration/configurations/${this.determineConfigurationId(
                requestOptions
            )}`,
            fetchParams
        )
    }

    /**
     * Start the configuration.
     * @param http {Http} the http wrapper
     * @param baseUrl {String}  the base url, e.g. http://localhost:8080/showcase/
     * @param options {Object}  the start options
     * @param language {String}  the language
     * @throws ConfigurationException when the configuration couldn't be started
     * @return {Promise<ConfigurationService>}
     */
    static create<T extends MappingProfile = MediumMappingProfile>(
        http: Http,
        baseUrl: string,
        options: any,
        language: (() => string) | string
    ): Promise<ConfigurationService<T>> {
        const lang = typeof language === "function" ? language : () => language
        if (options.configurationId) {
            const fetchParams = {
                method: "GET",
                headers: new Headers({
                    Accept: "application/vnd.encoway.conf.status+json",
                    "Accept-Language": lang()
                })
            }
            return http
                .fetch(
                    `${baseUrl}/api/configuration/configurations/${options.configurationId}`,
                    fetchParams
                )
                .then(handleException)
                .then((data) => {
                    if (!data.configurationId) {
                        throw new ResponseError(data.message, data)
                    }
                    return new ConfigurationService(
                        http,
                        baseUrl,
                        data,
                        lang,
                        true
                    )
                })
        }

        const fetchParams = {
            method: "POST",
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "Accept-Language": lang()
            }),
            body: JSON.stringify(options)
        }

        return http
            .fetch(`${baseUrl}/api/configuration/configurations`, fetchParams)
            .then((response) => response.json())
            .then((data) => {
                if (!data.configurationId) {
                    throw new ResponseError(data.message, data)
                }
                if (data.configurationStarted === false) {
                    throw new ResponseError(data.message, data)
                }
                if (data.conflictMessage) {
                    throw new ResponseError(data.conflictMessage, data)
                }
                return new ConfigurationService(http, baseUrl, data, lang, true)
            })
    }

    /**
     * Load a configuration.
     * @param http {http} the http-object to load the configuration with
     * @param baseUrl {String} the base url, e.g. http://localhost:8080/showcase/
     * @param args {Objects} the arguments
     * @param language {String} the language, e.g. de, en, ...
     * @throws Error when the configuration couldn't be started
     * @return {Promise<ConfigurationService>}
     */
    static load<T extends MappingProfile = MediumMappingProfile>(
        http: Http,
        baseUrl: string,
        args: any,
        language: (() => string) | string
    ): Promise<ConfigurationService<T>> {
        const lang = typeof language === "function" ? language : () => language
        const headers = new Headers({
            Accept: "application/json",
            "Content-Type": "application/vnd.encoway.conf.load+json",
            "Accept-Language": lang()
        })

        const fetchParams = {
            method: "POST",
            headers,
            body: JSON.stringify(args)
        }

        return http
            .fetch(`${baseUrl}/api/configuration/configurations`, fetchParams)
            .then((response) => response.json())
            .then((data) => {
                if (
                    !data.configurationId &&
                    !(
                        data.configurationStatus &&
                        data.configurationStatus.configurationId
                    )
                ) {
                    throw new ResponseError(data.message, data)
                }
                return new ConfigurationService(http, baseUrl, data, lang, true)
            })
    }

    /**
     * Returns the configuration id from request options, if set, otherwise the configuration id from current configuration.
     * @param {Object} requestOptions additional options for request
     * @return {String} id the configuration id
     */
    determineConfigurationId(requestOptions?: RequestOptions): string {
        return requestOptions?.configurationId ?? this.configurationId
    }
}
